import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * EditorialMarquee
 * One slow editorial marquee. Not fast, not noisy. It reads as a
 * printed strap-line moving through a magazine spread.
 *
 * Content: the LotWise operational vocabulary. Each pill separated by
 * a bullet glyph. Two rows for depth, opposite directions, at very
 * different opacities.
 */
const ITEMS = [
  "Photography",
  "Vehicle details",
  "Listing composition",
  "Cross-platform publishing",
  "Between-visit maintenance",
  "Sold-unit removal",
  "Price updates",
  "One dealership standard",
];

const BULLET = "\u2726";

export default function EditorialMarquee() {
  const prefersReducedMotion = useReducedMotion();
  const list = [...ITEMS, ...ITEMS];

  return (
    <section
      data-testid="editorial-marquee"
      className="relative overflow-hidden border-y border-white/10 bg-[#050609] py-10 text-white"
    >
      {/* Row 1 — foreground, slow */}
      <motion.div
        aria-hidden={false}
        className="flex gap-14 whitespace-nowrap will-change-transform"
        animate={prefersReducedMotion ? {} : { x: ["0%", "-50%"] }}
        transition={{
          duration: 55,
          ease: "linear",
          repeat: Infinity,
        }}
      >
        {list.map((item, i) => (
          <span
            key={`mq1-${i}`}
            className="inline-flex items-center gap-14 font-display text-[54px] font-bold tracking-[-0.03em] text-white md:text-[72px] lg:text-[92px]"
          >
            {item}
            <span className="text-[#4F0DFF]/70">{BULLET}</span>
          </span>
        ))}
      </motion.div>

      {/* Row 2 — background, opposite direction, dim */}
      <motion.div
        className="pointer-events-none mt-2 flex gap-14 whitespace-nowrap will-change-transform"
        animate={prefersReducedMotion ? {} : { x: ["-50%", "0%"] }}
        transition={{
          duration: 90,
          ease: "linear",
          repeat: Infinity,
        }}
      >
        {list.map((item, i) => (
          <span
            key={`mq2-${i}`}
            className="inline-flex items-center gap-14 font-display text-[32px] font-medium tracking-tight text-white/25 md:text-[40px]"
          >
            {item}
            <span className="text-white/15">{BULLET}</span>
          </span>
        ))}
      </motion.div>

      {/* Edge fade */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#050609] to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#050609] to-transparent"
      />
    </section>
  );
}
